/**
 * Client-side CUSIP parsing for the paste-box preview. It mirrors the backend's
 * `identifiers/cusip.py` (DESIGN §6 M1) token for token, so the count the analyst sees before
 * submitting is the count the server screens: same separators, same stripped punctuation, same
 * skipped header words and the same two deterministic repairs. The server re-parses the raw text
 * and its result is authoritative (it is also what gets recorded on the batch).
 *
 * Check digit: mod-10 "double-add-double". Values: 0-9 as-is, A=10..Z=35, '*'=36, '@'=37,
 * '#'=38; double every even 1-based position (2,4,6,8); add the base-10 digits of each value;
 * check = (10 - sum % 10) % 10. Verified vectors are in cusip.test.ts (muni research).
 */

const CUSIP_RE = /^[0-9A-Z*@#]{9}$/;
const BODY8_RE = /^[0-9A-Z*@#]{8}$/;
// `*`, `@` and `#` mark private placement numbers and only appear in positions 6-8; positions
// 1-5 are always [0-9A-Z] (backend `_ISSUER_CHARS`; the research vector 12345*@#7 has one in 6).
const ISSUER_RE = /^[0-9A-Z]{5}[0-9A-Z*@#]/;
// Excel turns a numeric-looking CUSIP such as 12345E105 into 1.2345E+109: unrecoverable.
const EXCEL_SCI_RE = /^\d(\.\d+)?E[+-]?\d+$/;
/** Header cells an Excel column paste drags along; skipped silently (backend `_HEADER_WORDS`). */
const HEADER_WORDS = new Set(["CUSIP", "CUSIPS", "CUSIP9", "CUSIP#", "ID", "SECURITY", "IDENTIFIER"]);
/** Separators: commas, semicolons, pipes and any whitespace (backend `_SPLIT`). */
const SPLIT_RE = /[,\s;|]+/;
/** Quote/bracket characters stripped from both ends of a token (backend `_STRIP_CHARS`). */
const STRIP_CHARS = new Set([..."'\"`‘’“”()[]{}<>"]);

export const LEADING_ZERO_NOTE = "leading zero restored (Excel drops leading zeros from numbers)";

function stripEnds(s: string): string {
  let a = 0;
  let b = s.length;
  while (a < b && STRIP_CHARS.has(s[a]!)) a += 1;
  while (b > a && STRIP_CHARS.has(s[b - 1]!)) b -= 1;
  return s.slice(a, b);
}

/** Strip whitespace and surrounding quotes/brackets, upper-case (backend `normalize`). */
export function normalizeCusip(raw: string): string {
  return stripEnds((raw ?? "").trim()).trim().toUpperCase();
}

function charValue(c: string): number {
  if (c >= "0" && c <= "9") return c.charCodeAt(0) - 48;
  if (c >= "A" && c <= "Z") return c.charCodeAt(0) - 55;
  if (c === "*") return 36;
  if (c === "@") return 37;
  if (c === "#") return 38;
  throw new Error(`invalid CUSIP character ${JSON.stringify(c)}`);
}

/** Check digit for the first 8 characters of a CUSIP. */
export function checkDigit(first8: string): number {
  const s = normalizeCusip(first8).slice(0, 8);
  if (!BODY8_RE.test(s)) throw new Error("need 8 CUSIP characters");
  let sum = 0;
  for (let i = 0; i < 8; i += 1) {
    let v = charValue(s[i]!);
    if (i % 2 === 1) v *= 2;
    sum += Math.floor(v / 10) + (v % 10);
  }
  return (10 - (sum % 10)) % 10;
}

/** Why a normalised token is not a valid CUSIP, or null when it is one. */
function invalidReason(s: string): string | null {
  if (s.length !== 9) return `wrong length (${s.length} characters; a CUSIP has 9)`;
  if (!CUSIP_RE.test(s)) {
    const bad = [...new Set([...s].filter((c) => !/[0-9A-Z*@#]/.test(c)))].sort().join("");
    return `invalid characters '${bad}'`;
  }
  if (!ISSUER_RE.test(s)) return "special characters * @ # are not allowed in the issuer number";
  if (!/\d/.test(s[8]!)) return "check digit (9th character) must be a digit";
  const expected = checkDigit(s);
  if (Number(s[8]) !== expected) return `check digit mismatch (expected ${expected}, got ${s[8]})`;
  return null;
}

export function isValidCusip(raw: string): boolean {
  return invalidReason(normalizeCusip(raw)) === null;
}

export interface InvalidCusip {
  input: string;
  reason: string;
}

/** A token accepted after a deterministic repair (reported, never silent). */
export interface RepairedCusip {
  input: string;
  cusip: string;
  note: string;
}

export interface ParsedCusipList {
  /** Valid, deduplicated, in first-seen order. This is what will be screened. */
  cusips: string[];
  invalid: InvalidCusip[];
  /** Valid CUSIPs that appeared more than once (listed once each). */
  duplicates: string[];
  /** 8-character inputs repaired: Excel's dropped leading zero, or a computed check digit. */
  repaired: RepairedCusip[];
}

/**
 * Backend `_repair`: an all-digit 8-character token whose "0"+token validates lost its leading
 * zero in Excel (037833100 -> 37833100), so that wins over appending a check digit (which would
 * silently point at a different issuer, 378331...). Otherwise an 8-character token is a CUSIP-8
 * and gets its computed check digit.
 */
function repair(s: string): { cusip: string | null; note: string | null; reason: string | null } {
  const reason = invalidReason(s);
  if (reason === null) return { cusip: s, note: null, reason: null };
  if (s.length !== 8) return { cusip: null, note: null, reason };
  if (/^\d{8}$/.test(s) && invalidReason(`0${s}`) === null) {
    return { cusip: `0${s}`, note: LEADING_ZERO_NOTE, reason: null };
  }
  if (BODY8_RE.test(s) && ISSUER_RE.test(s.slice(0, 6))) {
    const full = s + String(checkDigit(s));
    if (invalidReason(full) === null) {
      return { cusip: full, note: `8-character CUSIP completed with check digit ${full[8]}`, reason: null };
    }
  }
  return { cusip: null, note: null, reason };
}

/**
 * Split pasted text (commas, semicolons, tabs, whitespace, newlines, Excel cells) into CUSIPs.
 * Header words such as "CUSIP" are dropped silently because a column copied from Excel
 * usually includes its header.
 */
export function parseCusipList(text: string): ParsedCusipList {
  const tokens = (text ?? "").split(SPLIT_RE).filter((t) => stripEnds(t).trim());
  const seen = new Set<string>();
  const dupes = new Set<string>();
  const out: ParsedCusipList = { cusips: [], invalid: [], duplicates: [], repaired: [] };

  for (const token of tokens) {
    const s = normalizeCusip(token);
    if (!s || HEADER_WORDS.has(s)) continue;
    if (EXCEL_SCI_RE.test(s)) {
      out.invalid.push({ input: token, reason: "looks like Excel scientific notation; re-export the column as text" });
      continue;
    }
    const r = repair(s);
    if (r.cusip === null) {
      out.invalid.push({ input: token, reason: r.reason ?? "invalid" });
      continue;
    }
    if (seen.has(r.cusip)) {
      dupes.add(r.cusip);
      continue;
    }
    seen.add(r.cusip);
    out.cusips.push(r.cusip);
    if (r.note) out.repaired.push({ input: token, cusip: r.cusip, note: r.note });
  }
  out.duplicates = [...dupes];
  return out;
}

/** The single CUSIP in `text` (header quick-jump), repaired like the paste box, or null. */
export function singleCusip(text: string): string | null {
  const r = parseCusipList(text);
  return r.cusips.length === 1 && r.invalid.length === 0 ? r.cusips[0]! : null;
}

/** Server limits (backend `BatchCreateIn.cusips_text` max_length, `MAX_BATCH_CUSIPS`). */
export const MAX_CUSIPS_TEXT = 200_000;
export const MAX_BATCH_CUSIPS = 2000;

/**
 * Pull CUSIPs out of a dropped CSV/TSV/TXT file. When a header row has a "CUSIP" column only
 * that column is used, so a portfolio export's other numeric columns are not mistaken for
 * identifiers; otherwise the whole text is passed through.
 */
export function cusipTextFromFile(text: string): string {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return "";
  const delimiter = lines[0]!.includes("\t") ? "\t" : lines[0]!.includes(";") ? ";" : ",";
  const header = splitDelimited(lines[0]!, delimiter).map((h) => h.trim().toUpperCase());
  const col = header.findIndex((h) => h === "CUSIP" || h === "CUSIP9" || h === "CUSIP NUMBER");
  if (col < 0) return text;
  return lines
    .slice(1)
    .map((l) => splitDelimited(l, delimiter)[col] ?? "")
    .filter(Boolean)
    .join("\n");
}

/** Minimal quote-aware field splitter (enough for exported spreadsheets). */
export function splitDelimited(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i]!;
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (c === '"') {
        quoted = false;
      } else {
        cur += c;
      }
    } else if (c === '"') {
      quoted = true;
    } else if (c === delimiter) {
      out.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}
